import { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  useDeleteWorkCenter,
  useSaveWorkCenter,
  useSections,
  useWorkCenters,
} from '../../api/referentiel';
import type { ChargeSpread, WorkCenter, WorkCenterInput } from '../../api/types';
import {
  ApiErrorMessage,
  Modal,
  SelectField,
  TextField,
} from './ResourcesPanel';

const SPREAD_LABEL: Record<ChargeSpread, string> = {
  DEBUT: 'Au début',
  FIN: 'À la fin',
  REPARTIE: 'Répartie',
};

function emptyWorkCenter(): WorkCenterInput {
  return { code: '', label: '', active: true, sectionId: null, chargeSpread: 'REPARTIE' };
}

export default function WorkCentersPanel() {
  const { data: workCenters, isLoading, error } = useWorkCenters();
  const { data: sections } = useSections();
  const save = useSaveWorkCenter();
  const remove = useDeleteWorkCenter();
  const [editing, setEditing] = useState<{ id?: number; data: WorkCenterInput } | null>(null);

  if (isLoading) return <p className="text-gray-500">Chargement des centres de charge…</p>;
  if (error) return <p className="text-red-600">Erreur de chargement des centres de charge.</p>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Centres de charge</h2>
          <p className="text-xs text-gray-500">Maille de la charge/capacité. Le code doit correspondre au code ERP porté par les opérations.</p>
        </div>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded flex items-center gap-2"
          onClick={() => setEditing({ data: emptyWorkCenter() })}
        >
          <Plus size={18} /> Nouveau centre
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Libellé</th>
              <th className="px-4 py-3">Section</th>
              <th className="px-4 py-3">Répartition de charge</th>
              <th className="px-4 py-3">État</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {workCenters?.map((wc) => (
              <tr key={wc.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{wc.code}</td>
                <td className="px-4 py-3 text-gray-600">{wc.label}</td>
                <td className="px-4 py-3 text-gray-600">{wc.section?.label ?? '—'}</td>
                <td className="px-4 py-3 text-gray-600">{SPREAD_LABEL[wc.chargeSpread]}</td>
                <td className="px-4 py-3">
                  {wc.active ? (
                    <span className="text-green-700 text-xs">Actif</span>
                  ) : (
                    <span className="text-gray-400 text-xs">Inactif</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 justify-end">
                    <button className="text-blue-600 hover:text-blue-800" onClick={() => setEditing({ id: wc.id, data: toInput(wc) })} aria-label="Modifier">
                      <Pencil size={18} />
                    </button>
                    <button
                      className="text-red-600 hover:text-red-800"
                      onClick={() => {
                        if (confirm(`Supprimer le centre ${wc.code} ?`)) remove.mutate(wc.id);
                      }}
                      aria-label="Supprimer"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <WorkCenterModal
          value={editing}
          sections={sections ?? []}
          saving={save.isPending}
          error={save.error}
          onClose={() => {
            save.reset();
            setEditing(null);
          }}
          onSubmit={(data) =>
            save.mutate(
              { id: editing.id, data },
              {
                onSuccess: () => {
                  save.reset();
                  setEditing(null);
                },
              },
            )
          }
        />
      )}
    </div>
  );
}

function toInput(wc: WorkCenter): WorkCenterInput {
  return { code: wc.code, label: wc.label, active: wc.active, sectionId: wc.sectionId, chargeSpread: wc.chargeSpread };
}

function WorkCenterModal({
  value,
  sections,
  saving,
  error,
  onClose,
  onSubmit,
}: {
  value: { id?: number; data: WorkCenterInput };
  sections: { id: number; label: string }[];
  saving: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (data: WorkCenterInput) => void;
}) {
  const [form, setForm] = useState<WorkCenterInput>(value.data);
  const set = <K extends keyof WorkCenterInput>(key: K, v: WorkCenterInput[K]) => setForm((f) => ({ ...f, [key]: v }));

  return (
    <Modal title={value.id ? 'Modifier le centre' : 'Nouveau centre'} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(form);
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-4">
          <TextField label="Code" value={form.code} onChange={(v) => set('code', v)} required />
          <TextField label="Libellé" value={form.label} onChange={(v) => set('label', v)} required />
          <SelectField
            label="Section"
            value={form.sectionId == null ? '' : String(form.sectionId)}
            onChange={(v) => set('sectionId', v === '' ? null : Number(v))}
            options={[['', '— aucune —'], ...sections.map((s) => [String(s.id), s.label] as [string, string])]}
          />
          <SelectField
            label="Répartition de charge"
            value={form.chargeSpread}
            onChange={(v) => set('chargeSpread', v as ChargeSpread)}
            options={[['REPARTIE', 'Répartie (défaut)'], ['DEBUT', 'Au début'], ['FIN', 'À la fin']]}
          />
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} /> Centre actif
        </label>

        <ApiErrorMessage error={error} />

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="px-4 py-2 rounded border" onClick={onClose}>
            Annuler
          </button>
          <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50" disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
